import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = { code: "", name: "", type: "percent", value: 10, min_purchase: 0, active: true, outlet_ids: [] };

export default function Promos() {
  const [rows, setRows] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [p, o] = await Promise.all([api.get("/promos"), api.get("/outlets")]);
    setRows(p.data || []); setOutlets(o.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { ...form, value: Number(form.value), min_purchase: Number(form.min_purchase) };
      if (editId) await api.patch(`/promos/${editId}`, body);
      else await api.post("/promos", body);
      toast.success("Promo tersimpan"); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const del = async (id) => { if (!window.confirm("Hapus promo?")) return; await api.delete(`/promos/${id}`); load(); };

  return (
    <div className="p-6 lg:p-10">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline text-zinc-500">PROMOSI</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Promo & Diskon</h1>
        </div>
        <button onClick={()=>{setForm(empty);setEditId(null);setOpen(true);}} data-testid="promo-add-btn" className="px-4 py-2.5 bg-[#002FA7] text-white text-sm font-semibold inline-flex items-center gap-1"><Plus className="h-4 w-4"/>Promo Baru</button>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-border"><tr>
            <th className="text-left p-3 overline">Kode</th>
            <th className="text-left p-3 overline">Nama</th>
            <th className="text-left p-3 overline">Tipe</th>
            <th className="text-right p-3 overline">Nilai</th>
            <th className="text-right p-3 overline">Min. Belanja</th>
            <th className="text-left p-3 overline">Status</th>
            <th className="text-right p-3 overline">Aksi</th>
          </tr></thead>
          <tbody>
            {rows.length===0 && <tr><td colSpan={7} className="p-8 text-center text-zinc-500">Belum ada promo</td></tr>}
            {rows.map((p) => (
              <tr key={p.id} className="border-b border-border">
                <td className="p-3 font-mono font-bold">{p.code}</td>
                <td className="p-3">{p.name}</td>
                <td className="p-3 uppercase text-xs">{p.type}</td>
                <td className="p-3 text-right tabular-nums">{p.type==="percent"?`${p.value}%`:formatIDR(p.value)}</td>
                <td className="p-3 text-right tabular-nums">{formatIDR(p.min_purchase)}</td>
                <td className="p-3">{p.active ? <span className="text-emerald-700 text-xs uppercase">Aktif</span> : <span className="text-zinc-400 text-xs uppercase">Nonaktif</span>}</td>
                <td className="p-3 text-right">
                  <button onClick={()=>{setForm({code:p.code,name:p.name,type:p.type,value:p.value,min_purchase:p.min_purchase,active:p.active,outlet_ids:p.outlet_ids||[]});setEditId(p.id);setOpen(true);}} className="p-1.5 hover:bg-zinc-100" data-testid={`promo-edit-${p.id}`}><Pencil className="h-3.5 w-3.5"/></button>
                  <button onClick={()=>del(p.id)} className="p-1.5 hover:bg-rose-100 text-rose-600" data-testid={`promo-del-${p.id}`}><Trash2 className="h-3.5 w-3.5"/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>{editId?"Edit":"Buat"} Promo</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline">Kode</label><input data-testid="promo-form-code" value={form.code} onChange={(e)=>setForm({...form,code:e.target.value.toUpperCase()})} className="w-full border border-border px-3 py-2 mt-1 font-mono uppercase"/></div>
            <div><label className="overline">Nama</label><input data-testid="promo-form-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="w-full border border-border px-3 py-2 mt-1"/></div>
            <div><label className="overline">Tipe</label>
              <select data-testid="promo-form-type" value={form.type} onChange={(e)=>setForm({...form,type:e.target.value})} className="w-full border border-border px-3 py-2 mt-1">
                <option value="percent">Persentase</option><option value="amount">Potongan Rupiah</option>
              </select>
            </div>
            <div><label className="overline">Nilai</label><input data-testid="promo-form-value" type="number" value={form.value} onChange={(e)=>setForm({...form,value:e.target.value})} className="w-full border border-border px-3 py-2 mt-1 font-mono-num"/></div>
            <div className="col-span-2"><label className="overline">Min. Belanja</label><input data-testid="promo-form-min" type="number" value={form.min_purchase} onChange={(e)=>setForm({...form,min_purchase:e.target.value})} className="w-full border border-border px-3 py-2 mt-1 font-mono-num"/></div>
            <div className="col-span-2 flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e)=>setForm({...form,active:e.target.checked})} data-testid="promo-form-active"/><label>Aktif</label></div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={()=>setOpen(false)} className="flex-1 py-3 border border-border">Batal</button>
            <button onClick={save} data-testid="promo-save-btn" className="flex-1 py-3 bg-[#002FA7] text-white font-semibold">Simpan</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
